import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Snackbar, TextField, Typography } from "@mui/material";
import { createTheme } from '@mui/material/styles';
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Utilities from "../../common/Utilities";
import ConfigService from "../../services/ConfigService";
import LogService from "../../services/LogService";
import IAlert from "../../types/Alert";
import { CONFIGTYPES } from "../../types/Config";
import IAsistenciaDoc from "../../types/AsistenciaDoc";
import ILog from "../../types/Log";
import ConfigUtils from "../../utils/Config";
import * as constantes from "../../common/Constantes";
import ImageUploader from "../utilities/ImageUploader";

const defaultText = 'Se expide el presente justificante para acreditar la asistencia del paciente a la sesión en la fecha indicada.';

const JA = () => {
    const theme = createTheme();
    const history = useNavigate();

    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState(ConfigUtils.getAsistenciaDoc()?.texto || defaultText);

    const [sello, setSello] = useState<string | null>(ConfigUtils.getAsistenciaDoc()?.sello || null);

    const [asistenciaDoc, setAsistenciaDoc] = useState<IAsistenciaDoc | undefined | null>(ConfigUtils.getAsistenciaDoc());
    const [modalDeleteDoc, setModalDeleteDoc] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const clear = () => {
        if (asistenciaDoc) {
        setText(asistenciaDoc.texto || "");
        setSello(asistenciaDoc.sello || null);
    } else {
        setText(defaultText);
        setSello(null);
    }
    };

    const saveJustificante = () => {
        if (Utilities.isNullEmptyUndefined(text)) {
            setAlert({ open: true, severity: 'warning', msg: 'El texto en el justificante de asistencia es obligatorio' });
            return;
        }
        if (!sello) {
            setAlert({ open: true, severity: 'warning', msg: 'El sello en el justificante de asistencia es obligatorio' });
            return;
        }

        setLoading(true);
        const justificante: IAsistenciaDoc = {
            texto: text,
            sello: sello,
            userId: ConfigUtils.getUserId()
        };

        ConfigService.createAsistenciaDoc(justificante)
            .then((response: any) => {
                ConfigUtils.addConfig(CONFIGTYPES.asistenciaDoc.toString(), response.data);
                setAlert({ open: true, severity: 'success', msg: 'Justificante de asistencia guardado correctamente' });
                setAsistenciaDoc(response.data);
                setSello(response.data.sello);
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'JA.Component.createAsistenciaDoc', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const updateJustificante = () => {
        if (Utilities.isNullEmptyUndefined(text)) {
            setAlert({ open: true, severity: 'warning', msg: 'El texto en el justificante de asistencia es obligatorio' });
            return;
        }
        if (!sello) {
            setAlert({ open: true, severity: 'warning', msg: 'El sello en el justificante de asistencia es obligatorio' });
            return;
        }         

        setLoading(true);
        const justificante: IAsistenciaDoc = {
            texto: text,
            sello: sello,
            userId: ConfigUtils.getUserId()
        };

        ConfigService.updateAsistenciaDoc(asistenciaDoc?._id!, justificante)
            .then((response: any) => {
                ConfigUtils.editConfig(CONFIGTYPES.asistenciaDoc.toString(), response.data);
                setAlert({ open: true, severity: 'success', msg: 'Justificante de asistencia actualizado correctamente' });
                setAsistenciaDoc(response.data);
                setSello(response.data.sello);
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'JA.Component.updateAsistenciaDoc', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const deleteDoc = () => {
        setLoadingDelete(true);
        ConfigService.deleteAsistenciaDoc(ConfigUtils.getUserId())
        .then((response: any) => {
            setAsistenciaDoc(undefined);
            ConfigUtils.deleteConfig(CONFIGTYPES.asistenciaDoc.toString(), '');
            setAlert({ open: true, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'JA.Component.deleteDoc', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setModalDeleteDoc(false);
            setLoadingDelete(false);
        });
    };

    try {
    const hasChanges = asistenciaDoc
    ? text !== asistenciaDoc.texto || sello !== asistenciaDoc.sello
    : text !== defaultText || sello !== null;
        return (
    <Grid container>
        <Grid item xs={12}>
                {/* Título + botón eliminar */}
                <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: theme.spacing(2) }}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h6" sx={{ mt: theme.spacing(1), mb: theme.spacing(1), fontWeight: "bold", }}>
                            JUSTIFICANTE DE ASISTENCIA
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                        {asistenciaDoc && (
                            <Button onClick={() => setModalDeleteDoc(true)} variant="contained" color="error" >
                                ELIMINAR
                            </Button>
                        )}
                    </Grid>
                </Grid>

                {/* Contenido principal */}
                <Grid container spacing={2} alignItems="center">
                    {/* Campos de texto */}
                    <Grid item xs={12} md={9}>
                        <TextField
                            multiline
                            variant="filled"
                            fullWidth
                            value={`El paciente (Nombre y Apellidos) con DNI: (DNI o DNI del tutor legal)
Acudió a la clínica (Nombre de la clínica) el (Fecha - ej. Lunes, 2 de enero de 2023 de 15:00 a 16:00 horas.)`}
                            inputProps={{ readOnly: true }}
                        />

                        <TextField
                            multiline
                            rows={4}
                            variant="outlined"
                            fullWidth
                            autoFocus
                            sx={{ mt: theme.spacing(2) }}
                            value={text}
                            onChange={(event) => setText(event.target.value)}
                        />
                    </Grid>

                    {/* Sello */}
                    <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                        <ImageUploader
                            aspect={231 / 148}
                            label="Subir sello"
                            onCropped={(cropped) => setSello(cropped)}
                            initialImage={sello || null}
                            size={180}
                        />
                    </Grid>
                </Grid>

                {/* Botones de acción */}
                <Grid container sx={{ mt: 3 }}>
  {loading ? (
    <CircularProgress />
  ) : (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={{ xs: 1, sm: 2 }}
      width="100%"
    >
      {asistenciaDoc ? (
        <>
          <Button
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            variant="contained"
            color="primary"
            onClick={updateJustificante}
            disabled={!hasChanges || loading}
          >
            Actualizar cambios
          </Button>

          <Button
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            variant="outlined"
            color="secondary"
            onClick={clear}
            disabled={loading}
          >
            Cancelar cambios
          </Button>
        </>
      ) : (
        <>
          <Button
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            variant="contained"
            color="primary"
            onClick={saveJustificante}
            disabled={loading}
          >
            Guardar
          </Button>

          <Button
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            variant="outlined"
            color="secondary"
            onClick={clear}
            disabled={loading}
          >
            Cancelar
          </Button>
        </>
      )}
    </Box>
  )}
</Grid>
        </Grid>

        {/* ELIMINAR DOC */}
        <Dialog
            open={modalDeleteDoc}
            maxWidth="lg"
            onClose={() => setModalDeleteDoc(false)}
        >
            <DialogTitle>ELIMINAR JUSTIFICANTE DE ASISTENCIA</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    ¿Está seguro de que desea eliminar el justificante de asistencia?
                </DialogContentText>
            </DialogContent>
            {loadingDelete ? (
                <CircularProgress style={{ margin: '20px auto' }} />
            ) : (
                <DialogActions>
                    <Button onClick={deleteDoc} color="primary">
                        SI
                    </Button>
                    <Button
                        onClick={() => setModalDeleteDoc(false)}
                        color="primary"
                        autoFocus
                    >
                        NO
                    </Button>
                </DialogActions>
            )}
        </Dialog>

        {/* ALERTA */}
        <Snackbar
            open={alert.open}
            autoHideDuration={6000}
            onClose={handleCloseAlert}
        >
            <Alert
                variant="filled"
                onClose={handleCloseAlert}
                severity={alert.severity}
                sx={{ width: "100%" }}
            >
                {alert.msg}
            </Alert>
        </Snackbar>
    </Grid>
);

    } catch (e: any) {
        const error: ILog = { method: 'JA.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
};

export default JA;